import { Signal, inject } from '@angular/core';

import { QueryClient, injectQuery } from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { Notification } from 'src/app/core/models/core.models';

import { UserApiService } from '../services/user-api.service';

/**
 * The user's notifications - the list the header dropdown and the notifications
 * page render, plus the badge counting the unseen ones.
 *
 * The badge has **two** writers and the list shares one: a `newUserNotification`
 * arriving on the user socket prepends to the list *and* increments the badge,
 * while loading the list clears it. The list has a second socket writer - every
 * `app-notification` with `has_progress` subscribes to task progress and patches
 * its own row.
 */

const NOTIFICATIONS_ROOT_KEY = ['user', 'notifications'] as const;
const NOTIFICATIONS_COUNT_ROOT_KEY = ['user', 'notifications_count'] as const;

export interface NotificationsScope {
  userId: string;
  companyId: string;
}

/**
 * `companyId` is a key term so a company switch re-keys the entry: a live observer
 * refetches on a key change, not on a cache clear.
 */
export const notificationsQueryKey = ({
  userId,
  companyId,
}: NotificationsScope) =>
  [...NOTIFICATIONS_ROOT_KEY, { userId, companyId }] as const;

export const notificationsCountQueryKey = ({
  userId,
  companyId,
}: NotificationsScope) =>
  [...NOTIFICATIONS_COUNT_ROOT_KEY, { userId, companyId }] as const;

/**
 * The latest notifications - one fetch per session, on the first consumer to mount.
 *
 * `staleTime` and `gcTime` are both `Infinity`: task progress is patched into these
 * rows from the socket, and a background refetch mid-task would drop it back to
 * whatever the server last persisted.
 *
 * **The count reset lives in the `queryFn`**, and it is client-only -
 * `POST /user/:id/reset_counter` exists but has never been called, so a refetch
 * would resurrect a badge the user already cleared.
 */
export function injectLatestNotificationsQuery(
  scope: Signal<NotificationsScope | null>,
) {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? notificationsQueryKey(current)
        : ([...NOTIFICATIONS_ROOT_KEY, null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getLatestNotifications(current!.userId)).then(
          (notifications) => {
            queryClient.setQueryData<number>(
              notificationsCountQueryKey(current!),
              0,
            );

            return notifications ?? [];
          },
        ),
      enabled: !!current,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** The header's notification badge (GET /user/:id/notification_list_count). */
export function injectNotificationsCountQuery(
  scope: Signal<NotificationsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? notificationsCountQueryKey(current)
        : ([...NOTIFICATIONS_COUNT_ROOT_KEY, null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getNotificationsCount(current!.userId)),
      enabled: !!current,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * A notification pushed over the user socket. Both writes are partial-key sweeps
 * because `app.component` holds the socket callback and has no query scope, and
 * both entries outlive the header unmounting - so a notification arriving during
 * Presentation Mode still counts.
 */
export function prependUserNotification(
  queryClient: QueryClient,
  notification: Notification,
): void {
  queryClient.setQueriesData<Notification[]>(
    { queryKey: NOTIFICATIONS_ROOT_KEY },
    (current) => (current ? [notification, ...current] : current),
  );

  queryClient.setQueriesData<number>(
    { queryKey: NOTIFICATIONS_COUNT_ROOT_KEY },
    (current) => (current ?? 0) + 1,
  );
}

export interface NotificationProgress {
  overallProgress: number;
  overallProgressMax: number;
  overallProgressPercent: number;
  isFinished: boolean;
}

/**
 * A task-progress frame for one notification. A no-op when the notification is not
 * in the loaded list, which is the toast's case.
 */
export function patchNotificationProgress(
  queryClient: QueryClient,
  notificationId: string,
  progress: NotificationProgress,
): void {
  queryClient.setQueriesData<Notification[]>(
    { queryKey: NOTIFICATIONS_ROOT_KEY },
    (current) =>
      current?.map((notification) =>
        notification.fingerprint_id == notificationId
          ? {
              ...notification,
              overall_progress: progress.overallProgress,
              overall_progress_max: progress.overallProgressMax,
              overall_progress_percent: progress.overallProgressPercent,
              is_finished: progress.isFinished,
            }
          : notification,
      ),
  );
}
